/**
 * Canonical schemas for normalized character and monster data
 * 
 * date_of_change: 2025-09-02
 * purpose: Single source of truth interfaces following instructions blueprint
 * rule_reference: Sections 1.1-1.2 of instructions.txt - canonical shapes must exist before normalization
 */

// Base types for library resolution
export interface ResolvedReference {
  query: string;
  id: string | null;
  confidence: number;
  reason: string[];
}

export interface AuditTrail {
  normalizer: string;
  seed: string;
  decisions: Array<{
    header: string;
    decision: {
      category: string;
      confidence: number;
      reason: string[];
    };
  }>;
  error?: {
    message: string;
    stack?: string;
  };
}

// Character canonical schema
export interface CanonicalCharacter {
  id: string;
  name: string;
  
  // Core stats - canonical field names only
  level: number;
  class: string;
  race: string;
  subrace?: string;
  background: string;
  alignment?: string;
  
  // Abilities - full names only (no short names)
  abilities: {
    strength: number;
    dexterity: number;
    constitution: number;
    intelligence: number;
    wisdom: number;
    charisma: number;
  };
  
  // Combat stats
  hp: number;
  maxHp: number;
  ac: number;
  initiative: number;
  speed: number;
  proficiencyBonus: number;
  
  // Biography - structured canonical fields
  biography: {
    description?: string;
    appearance?: string;
    backstory?: string;
    personalityTraits?: string;
    ideals?: string;
    bonds?: string;
    flaws?: string;
  };
  
  // Features with backward compatibility and full embed support
  // @date_of_change: 2025-10-18 - Allow string and FullFeatureData in addition to legacy shape
  features: Array<{
    name: string;
    text: string;
    refId?: string | null;
    resolvedAtVersion?: number | null;
  } | string | FullFeatureData>;
  
  // Feats with backward compatibility and full embed support
  // @date_of_change: 2025-10-18 - Allow string and FullFeatData
  feats?: Array<{
    name: string;
    refId?: string | null;
    resolvedAtVersion?: number | null;
  } | string | FullFeatData>;
  
  // Spells with full embed pattern
  // @date_of_change: 2025-10-18 - Support string refs and FullSpellData; add slots
  spells: {
    ability?: 'intelligence' | 'wisdom' | 'charisma';
    attackBonus?: number;
    saveDC?: number;
    known?: Array<string | FullSpellData>;
    prepared?: Array<string | FullSpellData>;
    byLevel?: Record<number, Array<string | FullSpellData>>;
    slots?: Record<number, { total: number; used: number }>;
    atWill?: Array<string | FullSpellData>;
    perDay?: Array<{ times: number; spells: Array<string | FullSpellData> }>;
    resolved?: ResolvedReference[];
  };
  
  // Equipment with full embed pattern and items bucket
  // @date_of_change: 2025-10-18 - Add items; allow string refs or FullEquipmentData
  equipment?: {
    weapons?: Array<string | FullEquipmentData>;
    armor?: Array<string | FullEquipmentData>;
    items?: Array<string | FullEquipmentData>;
    /** @deprecated use items */
    other?: Array<string | FullEquipmentData>;
  };
  
  // Skills and saves - structured
  skills: Record<string, {
    proficient: boolean;
    expertise: boolean;
    value: number;
  }>;
  
  savingThrows: Record<string, boolean>;
  
  // Compatibility and audit
  compat?: Record<string, unknown>;
  _audit?: AuditTrail;
}

// Monster action-economy entries remain explicit so runtime code never has to infer
// executable mechanics from trait prose. D&D 5e: actions, bonus actions, and reactions
// use distinct timing resources. date_of_change: 2026-09-02.
/** Embedded, edition-preserving monster mechanics. SRD 5.1 (2014).
 * date_of_change: 2026-09-05. This file owns the contract in every tier.
 */
export interface CreatureAttack {
  type: 'meleeWeapon' | 'rangedWeapon' | 'meleeSpell' | 'rangedSpell';
  toHit: number;
  reach?: number;
  range?: { normal: number; long: number };
  damage: Array<{ dice: string; type: DamageType }>;
  notes?: string;
}
export interface CreatureEquipmentState { shieldEquipped: boolean; shieldArmorClassBonus: number }
export type CreatureMechanics =
  | { kind: 'display' }
  | { kind: 'attack'; onHit: Array<{ kind: 'saveCondition'; ability: keyof AbilityBlock; dc: number; condition: string }>; prerequisites: Array<{ kind: 'noShield' }> }
  | { kind: 'multiattack'; components: Array<{ actionId: string; count: number }> }
  | { kind: 'standard' }
  | { kind: 'moveToward'; speedMultiplier: number; requiresVisibleHostile: true }
  | { kind: 'grantsActions'; actionIds: string[] }
  | { kind: 'checkAdvantage'; ability: keyof AbilityBlock; skill: 'perception'; senses: Array<'sight' | 'hearing' | 'smell'> }
  | { kind: 'adjacentAllyAdvantage'; distanceFeet: number }
  | { kind: 'extraWeaponDie'; attackTypes: Array<'meleeWeapon' | 'rangedWeapon'>; count: number }
  | { kind: 'conditionalDamage'; dice: string; attackTypes: Array<'meleeWeapon' | 'rangedWeapon'>; allyWithinFeet?: number; surpriseRound?: number; usage?: { limit: 1; reset: 'turn' } }
  | { kind: 'surviveAtZero'; ability: keyof AbilityBlock; dcBase: number; excludeDamageTypes: DamageType[]; excludeCritical: boolean; hpOnSuccess: number };
export interface CreatureTrait {
  id: string;
  name: string;
  text: string;
  activation: 'passive' | 'trigger';
  mechanics: CreatureMechanics;
  ruleReference?: string;
}

/** JSON boundary extensions; semantic validation is validateCreatureInventory.
 * Keep schema ownership here, including fields preserved by the library validator.
 * date_of_change: 2026-09-05; rule_reference: AGENTS.md canonical schema authority.
 */
export const CREATURE_ACTION_JSON_FIELDS = {
  id: { type: 'string', minLength: 1 },
  activationType: { type: 'string', enum: ['action', 'bonus action', 'reaction'] },
  activation: { type: 'string', enum: ['passive', 'trigger'] },
  mechanics: { type: 'object' },
  attack: { type: 'object' },
  ruleReference: { type: 'string' }
};
export const CREATURE_DATA_JSON_FIELDS = {
  contentVersion: { type: 'string' },
  resistanceBlock: {
    type: 'object', additionalProperties: false, required: ['resist', 'immune', 'vuln'],
    properties: Object.fromEntries(['resist', 'immune', 'vuln'].map(key => [key, { type: 'array', items: { type: 'string' } }]))
  },
  conditionImmunities: { type: 'array', items: { type: 'string' } },
  savingThrows: { type: 'object', additionalProperties: { type: 'integer' } },
  equipmentState: { type: 'object', additionalProperties: false, required: ['shieldEquipped', 'shieldArmorClassBonus'], properties: { shieldEquipped: { type: 'boolean' }, shieldArmorClassBonus: { type: 'integer', minimum: 0, maximum: 10 } } }
};

export type CanonicalMonsterAction = {
  id?: string;
  mechanics?: CreatureMechanics;
  activationType?: 'action' | 'bonus action' | 'reaction';
  attack?: CreatureAttack;
  name: string;
  text: string;
  refId?: string | null;
  resolvedAtVersion?: number | null;
  standardActionKey?: string;
  executionRoute?: 'attack' | 'spell' | 'ability' | 'standard';
  targetRequirement?: 'none' | 'self' | 'creature' | 'object' | 'point' | 'area' | 'trigger';
  ruleReference?: string;
  featureKey?: string;
} | FullFeatureData | string;

// Monster canonical schema (excerpt parity)
export interface CanonicalMonster {
  id: string;
  name: string;
  
  // Core stats
  size: string;
  type: string;
  alignment: string;
  
  // Abilities - full names only
  abilities: {
    strength: number;
    dexterity: number;
    constitution: number;
    intelligence: number;
    wisdom: number;
    charisma: number;
  };
  
  // Combat stats
  hp: number;
  maxHp: number;
  ac: number;
  speed: Record<string, number>;
  initiative?: number;
  
  // Identity and lore - canonical fields
  data: {
    identity: {
      description?: string;
      lore?: string;
    };
  };
  
  // Actions with library resolution or full embeds
  actions: CanonicalMonsterAction[];
  traits?: CreatureTrait[];
  contentVersion?: string;
  resistanceBlock?: ResistanceBlock;
  conditionImmunities?: string[];
  equipmentState?: CreatureEquipmentState;
  bonusActions?: CanonicalMonsterAction[];
  reactions?: CanonicalMonsterAction[];
  
  // Spells for spellcasting monsters (full embed pattern)
  spells?: {
    ability?: 'intelligence' | 'wisdom' | 'charisma';
    attackBonus?: number;
    saveDC?: number;
    byLevel?: Record<number, Array<string | FullSpellData>>;
    slots?: Record<number, { total: number; used: number }>;
    atWill?: Array<string | FullSpellData>;
    resolved?: ResolvedReference[];
  };
  
  // Skills, saves, senses
  skills?: Record<string, number>;
  savingThrows?: Record<string, number>;
  senses?: Record<string, number>;
  
  // Challenge and proficiency
  challengeRating: number;
  proficiencyBonus: number;
  
  // Compatibility and audit
  compat?: Record<string, unknown>;
  _audit?: AuditTrail;
}

// @date_of_change: 2025-10-18 - Add expanded full-embed pattern types for single-source canonical schemas
// rule_reference: AGENTS.md — Canonical schemas are the single source of truth; support full object embeds
export interface FullSpellData {
  name: string;
  level: number;
  school: string;
  castingTime: string;
  range: string;
  components: string;
  material?: string;
  concentration?: boolean;
  duration: string;
  description: string;
  damage?: Array<{ dice: string; type: string }>;
  save?: { ability: 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma'; halfOnSuccess?: boolean };
  aoe?: { shape: 'sphere' | 'cube' | 'cylinder' | 'cone' | 'line'; size: string };
  /** Structured target legality used by the simulator; prose is never parsed for target rules. */
  target?: MechanicsTarget;
  refId?: string | null;
  resolvedAtVersion?: number | null;
}

export interface FullFeatureData {
  name: string;
  description: string;
  level?: number;
  source?: string;
  actionType?: 'action' | 'bonus' | 'reaction' | 'passive';
  standardActionKey?: string;
  executionRoute?: 'attack' | 'spell' | 'ability' | 'standard';
  targetRequirement?: 'none' | 'self' | 'creature' | 'object' | 'point' | 'area' | 'trigger';
  ruleReference?: string;
  featureKey?: string;
  /** Explicit timing and target fields keep runtime rules out of descriptions. */
  activation?: MechanicsActivation;
  target?: MechanicsTarget;
  uses?: { max: number; current: number; reset: 'short' | 'long' | 'dawn' | 'custom' };
  mechanics?: string;
  refId?: string | null;
  resolvedAtVersion?: number | null;
}

export interface FullFeatData {
  name: string;
  description: string;
  prerequisite?: string;
  benefits?: Array<{ type: string; description: string }>;
  refId?: string | null;
  resolvedAtVersion?: number | null;
}

export interface FullEquipmentData {
  name: string;
  type: 'weapon' | 'armor' | 'shield' | 'item' | 'consumable' | 'tool' | 'ammunition' | 'valuable';
  description: string;
  rarity?: 'common' | 'uncommon' | 'rare' | 'very rare' | 'legendary' | 'artifact';
  /** Explicit item classification; avoids inferring rules state from names or descriptions. */
  magical?: boolean;
  /** @deprecated use requiresAttunement for requirement state. */
  attunement?: boolean;
  requiresAttunement?: boolean;
  attuned?: boolean;
  properties?: string[];
  damage?: string;
  damageType?: string;
  attackBonus?: number;
  armorClass?: string | number;
  quantity?: number;
  weight?: string | number;
  equipped?: boolean;
  effects?: Array<{ type: string; description: string }>;
  /** Optional structured activation for usable equipment such as glamoured armor. */
  activation?: MechanicsActivation;
  target?: MechanicsTarget;
  refId?: string | null;
  resolvedAtVersion?: number | null;
}

/**
 * Canonical structured mechanics shared by features, spells, and equipment.
 * Runtime systems must not recover these values from descriptive prose.
 * date_of_change: 2026-08-11
 */
export interface MechanicsActivation {
  actionCost: 'action' | 'bonus_action' | 'reaction' | 'free' | 'passive' | 'minute' | 'hour' | 'special';
  durationSeconds?: number;
}

export interface MechanicsTarget {
  kinds: Array<'self' | 'creature' | 'object' | 'point' | 'area'>;
  rangeFeet?: number;
  touch?: boolean;
}

// @date_of_change: 2025-10-18 - Add expanded mechanics support types and unified CanonicalEntity
// rule_reference: Engine determinism & separation of concerns; types shared across engine subsystems
export type SpeedModes = {
  walk: number;
  fly?: number;
  swim?: number;
  climb?: number;
  burrow?: number;
  hover?: boolean;
};

export type Senses = {
  darkvision?: number;
  blindsight?: number;
  tremorsense?: number;
  truesight?: number;
  passivePerception?: number;
};

export type Condition =
  | 'blinded' | 'charmed' | 'deafened' | 'frightened' | 'grappled' | 'incapacitated' | 'invisible'
  | 'paralyzed' | 'petrified' | 'poisoned' | 'prone' | 'restrained' | 'stunned' | 'unconscious' | 'exhausted';

export type DamageType =
  | 'acid' | 'bludgeoning' | 'cold' | 'fire' | 'force' | 'lightning' | 'necrotic' | 'piercing'
  | 'poison' | 'psychic' | 'radiant' | 'slashing' | 'thunder';

export type ResistanceBlock = { resist?: DamageType[]; immune?: DamageType[]; vuln?: DamageType[] };

export type AbilityBlock = {
  strength: number; dexterity: number; constitution: number; intelligence: number; wisdom: number; charisma: number;
};

export type Resource = { key: string; label: string; current: number; max: number; resetOn?: 'short' | 'long' | 'dawn' | 'turn' };

export type Spell = {
  name: string;
  level?: number;
  school?: 'abjuration' | 'conjuration' | 'divination' | 'enchantment' | 'evocation' | 'illusion' | 'necromancy' | 'transmutation';
  components?: ('V' | 'S' | 'M')[];
  material?: string;
  castingTime?: 'action' | 'bonus' | 'reaction' | 'minute' | 'hour' | 'special';
  range?: string;
  duration?: string;
  concentration?: boolean;
  attackRoll?: boolean;
  saveType?: 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';
  damage?: string;
  healing?: string;
  area?: { shape: 'sphere' | 'cube' | 'cone' | 'line' | 'cylinder'; size: number };
  tags?: string[];
  description?: string;
  higherLevels?: string;
  original?: any;
};

export type Action = {
  name: string;
  type: 'attack' | 'spell' | 'feature' | 'item' | 'environment';
  attackBonus?: number;
  damageFormula?: string;
  healingFormula?: string;
  damageType?: DamageType;
  reach?: number;
  range?: number;
  saveType?: 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';
  saveDC?: number;
  area?: { shape: 'sphere' | 'cube' | 'cone' | 'line' | 'cylinder'; size: number };
  conditions?: Condition[];
  description?: string;
  original?: any;
};

// Unified canonical entity (long-term convergence type)
export type CanonicalEntity = {
  id: string;
  name: string;
  type: 'character' | 'monster';
  challenge?: number;
  hpObj: { current: number; max: number; temp?: number };
  ac: number | { base: number; shield?: number; cover?: number };
  speeds: SpeedModes;
  senses?: Senses;
  abilities: AbilityBlock;
  profBonus?: number;
  savingThrows?: Partial<Record<keyof AbilityBlock, boolean>>;
  skills?: Record<string, number>;
  spells?: { ability?: 'intelligence' | 'wisdom' | 'charisma'; attackBonus?: number; saveDC?: number; cantrips?: Spell[]; known?: Spell[]; prepared?: Spell[]; slots?: Record<number, { total: number; used: number }>; atWill?: Spell[] };
  actions?: Action[];
  resources?: Resource[];
  resistances?: ResistanceBlock;
  conditions?: Condition[];
  schemaVersion: number;
  original?: any;
};

// Reference library types
export type RefKind = "spell" | "feature" | "feat" | "species" | "background" | "item";

export interface RefEntry {
  id: string;
  slug: string;
  name: string;
  edition: "2014" | "2024" | "homebrew";
  source: string;
  type: RefKind;
  subtype?: string; // For items: "weapon", "armor", "equipment", "consumable"
  tags?: string[];
  aliases?: string[];
  version: number;
  text?: string;
  rules: any; // Type-specific payload
  deprecated?: boolean;
  replacedBy?: string; // id@version
}

// Spell rules payload
export interface SpellRules {
  level: number;
  school: string;
  castingTime: string;
  range: string;
  components: string;
  material?: string;
  concentration: boolean;
  duration: string;
  aoe?: {
    shape: "sphere" | "cube" | "cylinder" | "cone" | "line";
    size: string;
  };
  save?: {
    ability: "strength" | "dexterity" | "constitution" | "intelligence" | "wisdom" | "charisma";
    halfOnSuccess: boolean;
  };
  damage?: Array<{
    dice: string;
    type: string;
  }>;
}

// Feature rules payload
export interface FeatureRules {
  kind: "class_feature" | "species_trait" | "background_feature";
  class?: string;
  level?: number;
  uses?: {
    perRest: "short" | "long" | "dawn";
    formula: string;
  };
  actionEconomy?: "action" | "bonus" | "reaction" | "passive";
  effects?: Array<{
    type: "custom" | "damage" | "heal" | "condition";
    notes: string;
  }>;
}

// Feat rules payload
export interface FeatRules {
  prerequisites?: string[];
  benefits?: Array<{
    type: "custom" | "ability_score" | "skill" | "damage";
    notes: string;
  }>;
}

// Item rules payloads
export interface WeaponRules {
  category: "simple" | "martial";
  damage: Array<{
    dice: string;
    type: string;
  }>;
  versatile?: {
    dice: string;
  };
  properties: string[];
  masteries?: string[]; // 2024 masteries
  range?: {
    normal: number;
    long?: number;
  };
}

export interface ArmorRules {
  slot: "body" | "shield";
  armorType: "light" | "medium" | "heavy";
  baseAC: number;
  stealthDisadvantage?: boolean;
  strRequirement?: number;
}
